import React from 'react'
import {
  Alert,
  AlertIcon,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Link,
  NumberInput,
  NumberInputField,
  Stack,
  Text,
  Textarea
} from '@chakra-ui/react'
import { formatMoney } from 'lib/numbers'
import { BooksLockedNotice } from 'lib/buildiumBooksLock'
import { mapContentToBadge } from 'components/TailwindUIToolkit/badges'
import BuildiumPostingSummary from '../BuildiumPostingSummary'
import { UtilityBill, UtilityBillStatusDisplay } from './types'
import {
  formatDisplayDate,
  getBuildiumBillUrl,
  getStatusBadgeColor,
  getUtilityTypeLabel
} from './helpers'

type ChargebackDetailsStepProps = {
  amount: string
  booksLockDate?: string | null
  date: string
  errorMessage?: string
  isBooksLockError: boolean
  isBooksLockLoading: boolean
  isBuildiumBooksLocked: boolean
  note: string
  numericAmount: number
  onAmountChange: (value: string) => void
  onDateChange: (value: string) => void
  onNoteChange: (value: string) => void
  selectedBill: UtilityBill
  selectedBillStatus?: UtilityBillStatusDisplay
  warningMessage?: string
}

export default function ChargebackDetailsStep({
  amount,
  booksLockDate,
  date,
  errorMessage,
  isBooksLockError,
  isBooksLockLoading,
  isBuildiumBooksLocked,
  note,
  numericAmount,
  onAmountChange,
  onDateChange,
  onNoteChange,
  selectedBill,
  selectedBillStatus,
  warningMessage
}: ChargebackDetailsStepProps) {
  return (
    <>
      <Stack spacing={1}>
        <Text fontWeight='bold'>{getUtilityTypeLabel(selectedBill.utility_type.name)}</Text>
        <Text>
          Service period: {formatDisplayDate(selectedBill.service_start_date)} to{' '}
          {formatDisplayDate(selectedBill.service_end_date)}
        </Text>
        <Text>Bill date: {formatDisplayDate(selectedBill.bill_date)}</Text>
        <Text>Due date: {formatDisplayDate(selectedBill.bill_due_date)}</Text>
        <Text>Vendor: {selectedBill.vendor}</Text>
        <Text>Charge type: {getUtilityTypeLabel(selectedBill.charge_type)}</Text>
        <Text>Bill amount: {formatMoney(Number(selectedBill.amount), 2, '$')}</Text>
        {selectedBill.buildium_id && selectedBill.buildium_bank_account_id && (
          <Text>
            Buildium bill:{' '}
            <Link
              color='blue.500'
              href={getBuildiumBillUrl(
                selectedBill.buildium_bank_account_id,
                selectedBill.buildium_id
              )}
              isExternal
            >
              #{selectedBill.buildium_id}
            </Link>
          </Text>
        )}
        <HStack>
          <Text>Status:</Text>
          {selectedBillStatus &&
            mapContentToBadge(selectedBillStatus.description, {
              dot: false,
              size: 'xs',
              colorOverride: getStatusBadgeColor(selectedBillStatus.colorScheme),
              formatFn: (value) => value
            })}
        </HStack>
      </Stack>

      {warningMessage && (
        <Alert status='warning'>
          <AlertIcon />
          {warningMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert status='error'>
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      <HStack alignItems='start'>
        <FormControl isRequired>
          <FormLabel>Amount</FormLabel>
          <NumberInput min={0.01} precision={2} value={amount} onChange={onAmountChange}>
            <NumberInputField />
          </NumberInput>
        </FormControl>
        <FormControl isRequired isInvalid={isBooksLockError || isBuildiumBooksLocked}>
          <FormLabel>Date</FormLabel>
          <Input type='date' value={date} onChange={(event) => onDateChange(event.target.value)} />
        </FormControl>
      </HStack>

      {isBooksLockLoading && <Text fontSize='sm'>Checking Buildium books-lock status...</Text>}
      {(isBooksLockError || isBuildiumBooksLocked) && (
        <BooksLockedNotice lockDate={booksLockDate} isError={isBooksLockError} />
      )}

      <FormControl>
        <FormLabel>Note</FormLabel>
        <Textarea
          value={note}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder='Optional note'
        />
      </FormControl>

      <BuildiumPostingSummary
        amount={numericAmount}
        date={date}
        entryDescription='Utility chargeback liability'
        isBooksLockError={isBooksLockError}
        isBooksLockLoading={isBooksLockLoading}
        isBuildiumBooksLocked={isBuildiumBooksLocked}
        lockDate={booksLockDate}
        typeLabel={getUtilityTypeLabel(selectedBill.utility_type.name)}
      />
    </>
  )
}
